import React, { useState } from "react";
import logo from "./assets/Logo.png";

const Support = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [error, setError] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSuccessMessage("");
    setError("");

    // Simulate form submission
    try {
      // In a real scenario, this would be an API call
      setTimeout(() => {
        setSuccessMessage("Your message has been sent successfully! We'll get back to you soon.");
        setFormData({ name: "", email: "", subject: "", message: "" });
        setLoading(false);
      }, 1500);
    } catch (err) {
      setError("Something went wrong. Please try again later.");
      setLoading(false);
    }
  };

  const faqs = [
    {
      question: "How do I reset my password?",
      answer: "To reset your password, go to the login page and click on 'Forgot Password'. Follow the instructions sent to your registered email address.",
    },
    {
      question: "How can I add a new customer?",
      answer: "You can add a new customer by navigating to the 'Add Customer' section in the app or by using the public registration form available via QR code.",
    },
    {
      question: "Where can I view my recent orders?",
      answer: "All your orders are listed in the 'Orders' tab on your dashboard. You can filter them by status or date.",
    },
    {
      question: "Is my data secure?",
      answer: "Yes, we use industry-standard encryption and security protocols to ensure your data is always protected.",
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50 font-sans text-gray-900">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          <div className="flex items-center">
            <img src={logo} alt="KarigarHQ" className="h-10 w-auto" />
            <span className="ml-3 text-xl font-bold tracking-tight text-gray-900">KarigarHQ</span>
          </div>
          <button 
            onClick={() => window.location.href = "/"}
            className="text-sm font-medium text-gray-600 hover:text-cyan-600 transition-colors"
          >
            Back to Home
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 mb-4 tracking-tight">
            How can we <span className="text-cyan-500">help you?</span>
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Get in touch with our support team or browse through our frequently asked questions to find quick answers.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Left Column: Contact Info & FAQ */}
          <div className="space-y-12">
            {/* Contact Cards */}
            <section>
              <h2 className="text-2xl font-bold mb-6 flex items-center">
                <span className="w-8 h-8 bg-cyan-100 text-cyan-600 rounded-lg flex items-center justify-center mr-3">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                    <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                  </svg>
                </span>
                Contact Information
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                  <h3 className="font-semibold text-gray-500 text-sm uppercase tracking-wider mb-2">Email Us</h3>
                  <p className="text-lg font-bold text-gray-900">info@upheadstrategy.com</p>
                  <p className="text-sm text-gray-500 mt-1">Response within 24 hours</p>
                </div>
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                  <h3 className="font-semibold text-gray-500 text-sm uppercase tracking-wider mb-2">Business Hours</h3>
                  <p className="text-lg font-bold text-gray-900">Mon - Fri, 9am - 6pm</p>
                  <p className="text-sm text-gray-500 mt-1">Standard Time (IST)</p>
                </div>
              </div>
            </section>

            {/* FAQ Section */}
            <section>
              <h2 className="text-2xl font-bold mb-6 flex items-center">
                <span className="w-8 h-8 bg-cyan-100 text-cyan-600 rounded-lg flex items-center justify-center mr-3">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                  </svg>
                </span>
                Frequently Asked Questions
              </h2>
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <div key={index} className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                    <h3 className="text-lg font-bold text-gray-900 mb-2">{faq.question}</h3>
                    <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                  </div>
                ))}
              </div>
            </section>
          </div>

          {/* Right Column: Contact Form */}
          <div className="relative">
            <div className="sticky top-28 bg-white p-8 rounded-3xl shadow-xl border border-gray-100 overflow-hidden">
              {/* Decorative background element */}
              <div className="absolute -top-10 -right-10 w-40 h-40 bg-cyan-50 rounded-full blur-3xl opacity-50"></div>
              
              <div className="relative z-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Send us a message</h2>
                <p className="text-gray-600 mb-8">We're here to help with any technical issues or general questions.</p>

                <form onSubmit={handleSubmit} className="space-y-6">
                  {successMessage && (
                    <div className="bg-green-50 border-l-4 border-green-500 text-green-700 p-4 rounded-xl text-sm">
                      {successMessage}
                    </div>
                  )}
                  {error && (
                    <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-4 rounded-xl text-sm">
                      {error}
                    </div>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Name</label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Your full name"
                        className="block w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-cyan-400 focus:ring-4 focus:ring-cyan-400 focus:ring-opacity-10 outline-none transition-all bg-gray-50 placeholder-gray-400 text-gray-900"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Email</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="Your email address"
                        className="block w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-cyan-400 focus:ring-4 focus:ring-cyan-400 focus:ring-opacity-10 outline-none transition-all bg-gray-50 placeholder-gray-400 text-gray-900"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Subject</label>
                    <input
                      type="text"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      placeholder="What can we help you with?"
                      className="block w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-cyan-400 focus:ring-4 focus:ring-cyan-400 focus:ring-opacity-10 outline-none transition-all bg-gray-50 placeholder-gray-400 text-gray-900"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Message</label>
                    <textarea
                      name="message"
                      rows="4"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Describe your issue or question in detail..."
                      className="block w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-cyan-400 focus:ring-4 focus:ring-cyan-400 focus:ring-opacity-10 outline-none transition-all bg-gray-50 placeholder-gray-400 text-gray-900 resize-none"
                      required
                    ></textarea>
                  </div>

                  <button
                    type="submit"
                    className={`w-full py-4 text-white font-bold rounded-xl shadow-lg transition-all transform active:scale-95 ${
                      loading 
                        ? "bg-gray-400 cursor-not-allowed" 
                        : "bg-black hover:bg-gray-800 hover:shadow-cyan-400/20"
                    } flex items-center justify-center`}
                    disabled={loading}
                  >
                    {loading ? (
                      <span className="flex items-center">
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Sending...
                      </span>
                    ) : (
                      "Send Message"
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-100 py-12 mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-gray-500 text-sm">
            © {new Date().getFullYear()} KarigarHQ. All rights reserved.
          </p>
          <div className="mt-4 flex justify-center space-x-6">
            <a href="/ios_privacy_policy" className="text-sm text-gray-400 hover:text-cyan-500 transition-colors">Privacy Policy</a>
            <a href="/support" className="text-sm text-gray-400 hover:text-cyan-500 transition-colors">Support</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Support;
